<template>
  <div>
    <!-- Mobile: Undo/Redo and Restart Above Toolbar -->
    <div
      class="mobile-controls-bar tw-flex tw-items-center tw-justify-center tw-gap-4 tw-px-6 tw-mb-1 lg:tw-hidden lg:tw-mb-0">
      <slot name="undoRedo"></slot>
      <slot name="restart"></slot>
    </div>

    <div class="toolbar tw-flex tw-w-full tw-max-w-full tw-min-w-0 tw-items-center">
      <!-- Left: scale slider + undo + restart (one wrapper, desktop only) -->
      <div class="tw-hidden lg:tw-flex tw-flex-col tw-items-center tw-gap-4 tw-flex-shrink-0">
        <div>
          <slot name="undoRedo"></slot>
          <slot name="restart"></slot>
        </div>
        <slot name="scaleSlider"></slot>
      </div>

      <!-- Center: navigacija – 4 dugmeta -->
      <div class="toolbar-center tw-flex tw-overflow-x-auto tw-gap-2 tw-flex-1 tw-justify-center tw-min-w-0"
        :class="scrollbarClasses">
        <button class="toolbarButton tw-cursor-pointer"
          :class="{ 'toolbarButton--selected': showBackgroundPanel || showBackgroundImagesPanel }"
          @click="openBackgroundPanel">
          <svg class="toolbarButton__icon"
            :class="{ 'toolbarButton__icon--selected': showBackgroundPanel || showBackgroundImagesPanel }"
            viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" :aria-label="$t('background')">
            <path
              d="M27.3254 6.99414L28.4257 8.09431V13.6564L27.3254 14.7566H16.0848L15.5336 15.3079V16.176H14.3237V14.8067L15.5837 13.5468H26.8242L27.2158 13.1551V8.59545L26.8244 8.20397H26.3824V6.99414H27.3254Z"
              fill="currentColor" />
            <path
              d="M18.2052 17.3485L17.3528 16.4961H12.4784L11.626 17.3485V28.0031L12.4784 28.8555H17.3528L18.2052 28.0031V17.3485Z"
              fill="currentColor" />
            <path
              d="M25.1834 10.8761L26.0357 10.0237V5.14925L25.1834 4.29688H5.39241L4.54004 5.14924V10.0237L5.39241 10.8761H25.1834Z"
              fill="currentColor" />
          </svg>
          <span class="toolbarButton__label">{{ $t("background") }}</span>
        </button>

        <button class="toolbarButton tw-cursor-pointer" :class="{ 'toolbarButton--selected': showTemplatesPanel }"
          @click="openTemplatesPanel"
          v-if="config.variant === 'plastic-white' && hasTemplates">
          <img :src="getAssetPath('/assets/images/template.svg')" :alt="$t('templates')" class="toolbarButton__icon"
            :class="{ 'toolbarButton__icon--selected': showTemplatesPanel }" />
          <span class="toolbarButton__label">{{ $t("templates") }}</span>
        </button>

        <button class="toolbarButton tw-cursor-pointer" :class="{ 'toolbarButton--selected': showAddImagesPanel }"
          @click="openAddImagePanel">
          <svg class="toolbarButton__icon" :class="{ 'toolbarButton__icon--selected': showAddImagesPanel }"
            viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" :aria-label="$t('addImage')">
            <path
              d="M28.5 7V25L25.5 28H7.5L4.5 25V7L7.5 4H25.5L28.5 7ZM15 20.5625L10.875 19.125L7 21.875V23.125L9.375 25.5H23.625L26 23.125V19.3125L20.375 16.375L15 20.5625ZM10.4121 11.7666C9.14384 11.9188 8.23895 13.0707 8.3916 14.3389C8.5443 15.6071 9.69665 16.5116 10.9648 16.3584C12.2323 16.2053 13.136 15.0536 12.9834 13.7861C12.8308 12.5187 11.6797 11.6146 10.4121 11.7666Z"
              fill="currentColor" />
          </svg>
          <span class="toolbarButton__label">{{ $t("addImage") }}</span>
        </button>

        <button class="toolbarButton tw-cursor-pointer" :class="{ 'toolbarButton--selected': showTextPanel }"
          @click="onAddText">
          <svg class="toolbarButton__icon" :class="{ 'toolbarButton__icon--selected': showTextPanel }"
            viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" :aria-label="$t('addText')">
            <g clip-path="url(#icon-text-clip)">
              <path
                d="M28.7368 28.6992H3.50342V27.9023H28.7368V28.6992ZM29.1997 8.43945L11.5903 25.3857L3.62744 27.8105L6.35596 19.9463L23.9653 3L29.1997 8.43945Z"
                fill="currentColor" />
            </g>
            <defs>
              <clipPath id="icon-text-clip">
                <rect width="25.7" height="25.7" fill="white" transform="translate(3.5 3)" />
              </clipPath>
            </defs>
          </svg>
          <span class="toolbarButton__label">{{ $t("addText") }}</span>
        </button>

        <button class="toolbarButton tw-cursor-pointer" :class="{ 'toolbarButton--selected': showStickersPanel }"
          @click="openStickersPanel" v-if="hasStickers">
          <svg class="toolbarButton__icon" :class="{ 'toolbarButton__icon--selected': showStickersPanel }"
            viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" :aria-label="$t('stickers')">
            <path
              d="M28 16L22.4131 21.5234L16 28H4V4H28V16ZM6.7998 25.2002H14.5996V14.5996H25.2002V6.7998H6.7998V25.2002ZM17.4004 17.4004V22.6064L20.4229 19.5537L20.4443 19.5322L22.6006 17.4004H17.4004Z"
              fill="currentColor" />
            <mask id="icon-stickers-mask" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="6" y="6" width="21"
              height="21">
              <path
                d="M26.8574 14.5996V16L26.5 7L22 6.5L16 7L6.5 9.5L7.5 18V23.5L12 25.5V26.667H14.5996V14.5996H26.8574Z"
                fill="white" />
            </mask>
            <g mask="url(#icon-stickers-mask)">
              <path
                d="M16.0447 16.0164L16.9231 15.1507L18.5608 15.6974C19.6328 16.0619 20.1687 16.7301 20.1687 17.6717C20.1687 18.3855 19.9007 18.8563 19.3648 19.4638L19.201 19.646L19.2605 19.2208C19.4988 17.6717 19.0521 17.0035 17.5782 16.5175L16.0447 16.0164ZM13.8412 10.7161L18.3077 12.2348L17.34 13.1764L15.0174 12.3867C14.2134 12.1133 13.9454 11.6729 13.8412 10.7465V10.7161ZM13.5732 18.4311L14.5856 17.4439L16.4913 18.0818C17.4888 18.4159 17.8313 18.8563 17.727 19.965L13.5732 18.4311ZM12.2928 14.0269C12.2928 13.7383 12.4417 13.465 12.6948 13.2372C12.9628 13.632 13.4243 13.9813 14.1538 14.2243L15.732 14.7558L14.8536 15.6215L13.3052 15.1051C12.5906 14.8622 12.2928 14.4977 12.2928 14.0269ZM16.9677 22C20.2432 19.7827 22 18.2792 22 16.4264C22 15.1963 21.2854 14.5129 19.7072 13.9813L18.5161 13.5713L21.7767 10.382L21.1216 9.66822L20.1538 10.5339L15.5831 9C14.1687 9.47079 12.3821 10.8528 12.3821 12.2348C12.3821 12.3867 12.397 12.5386 12.4417 12.7056C11.2655 13.389 10.7891 14.0269 10.7891 14.8166C10.7891 15.5607 11.1762 16.3049 12.4119 16.715L13.3945 17.0491L10 20.375L10.6551 21.0888L11.7122 20.1016L16.9677 22Z"
                fill="currentColor" />
            </g>
          </svg>
          <span class="toolbarButton__label">{{ $t("stickers") }}</span>
        </button>
      </div>

      <!-- Right: CTA (slot iz Editora: SplitButton + Add to Cart) -->
      <div class="toolbar-right tw-hidden lg:tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
        <slot name="footerRight"></slot>
      </div>
    </div>

    <AddImagesPanel ref="addImagesPanel" :show-panel="showAddImagesPanel"
      @update:showPanel="(v) => { showAddImagesPanel = v; syncPanelOpenToParent(); }"
      @onImageSelect="(url) => onFileUpload(url, 'image')" />

    <BackgroundImagesPanel ref="backgroundImagesPanel" :show-panel="showBackgroundImagesPanel"
      @update:showPanel="(v) => { showBackgroundImagesPanel = v; syncPanelOpenToParent(); }"
      @onImageSelect="(url) => onFileUpload(url, 'background')" />

    <BackgroundPanel :showBackgroundPanel="showBackgroundPanel" :showColorPicker="showColorPicker"
      @onBackgroundImageUpload="
        () => {
          closeAllPanels();
          showBackgroundImagesPanel = true;
          syncPanelOpenToParent();
        }
      " @showColorPicker="(v) => (showColorPicker = v)"
      @showBackgroundPanel="(v) => { showBackgroundPanel = v; syncPanelOpenToParent(); }"
      @onColorPicked="setBackgroundColor" @onPatternPicked="setBackgroundPattern" />

    <TextPanel ref="textPanel" :showTextPanel="showTextPanel" :showColorPicker="showColorPicker"
      :selectedElement="selectedElement" :companyFonts="[]" @showColorPicker="(v) => (showColorPicker = v)"
      @showTextPanel="(v) => { showTextPanel = v; syncPanelOpenToParent(); }" @onFontSizeChange="onFontSizeChange"
      @onFontPick="showFontPanel = true" />

    <TemplatesPanel ref="templates" :showTemplatesPanel.sync="showTemplatesPanel" :config="config"
      @showTemplatesPanel="(v) => { showTemplatesPanel = v; syncPanelOpenToParent(); }"
      @onTemplateSelected="(template) => $emit('onTemplateSelected', template)"
      @templatesLoaded="(loaded) => (hasTemplates = loaded)" />

    <StickersPanel ref="stickers" :showStickersPanel="showStickersPanel"
      @showStickersPanel="(v) => { showStickersPanel = v; syncPanelOpenToParent(); }"
      @onStickerSelected="(sticker) => $emit('onStickerSelected', sticker)"
      @stickersLoaded="(loaded) => (hasStickers = loaded)" />
  </div>
</template>

<script>
import AddImagesPanel from "./AddImagesPanel.vue";
import BackgroundImagesPanel from "./BackgroundImagesPanel.vue";
import BackgroundPanel from "./BackgroundPanel.vue";
import TextPanel from "./TextPanel.vue";
import TemplatesPanel from "./TemplatesPanel.vue";
import StickersPanel from "./StickersPanel.vue";
import controlsVisibility from "@/constants/controlsVisibility";
import { colors } from "@/constants/colors";
import { canvasWidth } from "@/constants/canvas";
import { nextTick } from "vue";
import debounce from "@/utils/helpers/debounce";
import { getAssetPath } from "@/utils/assetPath";
import { scrollbarClasses } from "@/utils/tailwindScrollbarClasses";
import { trackEvent } from "@/utils/tracking";

export default {
  components: {
    AddImagesPanel,
    BackgroundImagesPanel,
    BackgroundPanel,
    TextPanel,
    TemplatesPanel,
    StickersPanel,
  },
  setup() {
    return {
      getAssetPath,
      scrollbarClasses,
    };
  },
  props: {
    isFlipped: {
      required: true,
      type: Boolean,
      default: false,
    },
    isPanelOpen: {
      type: Boolean,
      default: true,
    },
    selectedElement: {
      required: true,
    },
    helperElement: {
      required: true,
    },
    config: {
      required: true,
    },
  },
  computed: {
    anyPanelOpen() {
      return (
        this.showBackgroundPanel ||
        this.showBackgroundImagesPanel ||
        this.showAddImagesPanel ||
        this.showTemplatesPanel ||
        this.showStickersPanel ||
        this.showTextPanel
      );
    },
  },
  watch: {
    selectedElement: {
      handler() {
        if (this.selectedElement) {
          this.showTextPanel = this.selectedElement.type
            ?.toLowerCase()
            .includes("text");
          this.$nextTick(() => this.syncPanelOpenToParent());
        } else {
          // No selection (flip side or click empty) – close only TextPanel, keep others open
          this.showTextPanel = false;
        }
      },
      deep: true,
    },
  },
  data() {
    return {
      color: colors.black,
      showColorPicker: false,
      showAddImagesPanel: false,
      showBackgroundImagesPanel: false,
      showBackgroundPanel: false,
      showFontPanel: false,
      showTextPanel: false,
      showTemplatesPanel: false,
      showStickersPanel: false,
      hasTemplates: false,
      hasStickers: false,
    };
  },
  methods: {
    loadTemplates() {
      this.$refs.templates.getTemplates();
    },

    fireDebouncedModify() {
      // Will be set in mounted
    },

    closeAllPanels() {
      this.showTemplatesPanel = false;
      this.showAddImagesPanel = false;
      this.showBackgroundImagesPanel = false;
      this.showTextPanel = false;
      this.showBackgroundPanel = false;
      this.showStickersPanel = false;
      this.syncPanelOpenToParent();
    },

    syncPanelOpenToParent() {
      this.$nextTick(() => {
        this.$emit("panel-open-change", this.anyPanelOpen);
      });
    },

    openBackgroundPanel() {
      this.closeAllPanels();
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "OpenBackgroundPanel",
      });
      this.showBackgroundPanel = true;
      this.syncPanelOpenToParent();
    },

    openTemplatesPanel() {
      this.closeAllPanels();
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "OpenTemplatesPanel",
      });
      this.showTemplatesPanel = true;
      this.syncPanelOpenToParent();
    },

    openAddImagePanel() {
      this.closeAllPanels();
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "OpenAddImagePanel",
      });
      this.uploadType = "image";
      this.showAddImagesPanel = true;
      this.syncPanelOpenToParent();
    },

    openStickersPanel() {
      this.closeAllPanels();
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "OpenStickersPanel",
      });
      this.showStickersPanel = true;
      this.syncPanelOpenToParent();
    },

    // Helper: position background image and cleanup old ones
    positionBackgroundImage(img) {
      const MAX_BACKGROUNDS = 5;

      // Send new image to back, then send all others behind it
      window.activeCanvas.sendObjectToBack(img);
      const otherBackgrounds = window.activeCanvas
        .getObjects()
        .filter((obj) => obj.isBackgroundImage && obj !== img);
      otherBackgrounds.forEach((bg) =>
        window.activeCanvas.sendObjectToBack(bg),
      );

      // Set selection frame
      window.activeCanvas.setActiveObject(img);
      window.activeCanvas.renderAll();

      // Keep only last MAX_BACKGROUNDS
      const allBackgrounds = window.activeCanvas
        .getObjects()
        .filter((obj) => obj.isBackgroundImage);
      if (allBackgrounds.length > MAX_BACKGROUNDS) {
        const toRemove = allBackgrounds.slice(
          0,
          allBackgrounds.length - MAX_BACKGROUNDS,
        );
        toRemove.forEach((bg) => window.activeCanvas.remove(bg));
      }
    },

    onFontSizeChange(fontSize) {
      this.helperElement.set("fontSize", fontSize);
      window.helperCanvas.renderAll();
    },

    async setBackgroundPattern(pattern) {
      this.trackChangeBackground("pattern");
      const img = await window.fabric.FabricImage.fromURL(pattern, {
        crossOrigin: "anonymous",
      });
      img.set({
        isBackgroundImage: true,
      });
      window.activeCanvas.add(img);
      window.activeCanvas.viewportCenterObject(img);
      this.positionBackgroundImage(img);
    },

    setBackgroundColor(color) {
      this.trackChangeBackground("color");
      window.activeCanvas.backgroundImage = null;
      window.activeCanvas.set("backgroundColor", color);

      // Only color the active side, never sync to the other
      const norm = (c) => {
        const s = (c || "").replace(/^#/, "").toLowerCase();
        return s.length === 3 ? s.replace(/(.)/g, "$1$1") : s;
      };
      const isWhite = norm(color) === "ffffff";
      const isBlack = norm(color) === "000000";
      if (isWhite || isBlack) {
        this.invertColors(isWhite ? colors.white : colors.black);
      }

      window.activeCanvas.renderAll();
      this.fireDebouncedModify();
      this.$emit("preview-update-needed");
    },

    async invertColors(color) {
      const defaultText = window.frontCanvas
        .getObjects()
        .find((obj) => obj.id === "defaultText");

      if (window.activeCanvas === window.frontCanvas) {
        if (defaultText) {
          defaultText.fill = color === colors.white ? colors.black : colors.white;
          defaultText.dirty = true;
        }
      }
    },

    async uploadBackground(event) {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "UploadBackgroundImage",
      });
      const file = event.target.files[0];
      let image;

      if (file.type.includes("svg")) {
        // Read SVG as data URL
        const svgDataUrl = await readFileAsDataURL(file);
        // Convert SVG to PNG
        image = await convertSvgToPng(svgDataUrl);
      } else {
        image = await resizeImage(file, 1280, 1280);
      }

      const img = await window.fabric.FabricImage.fromURL(image, {
        crossOrigin: "anonymous",
      });
      img.set({
        cornerStyle: "circle",
        isBackgroundImage: true,
      });

      img.setControlsVisibility(controlsVisibility);
      img.scaleToWidth(canvasWidth);

      window.activeCanvas.add(img);
      window.activeCanvas.viewportCenterObject(img);
      this.positionBackgroundImage(img);

      this.fireDebouncedModify();
      this.$refs.backgroundRef.value = "";
    },

    onAddText() {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "AddText",
      });
      this.closeAllPanels();

      if (this.selectedElement?.type?.toLowerCase().includes("text")) {
        this.selectedElement.exitEditing();
        window.activeCanvas.discardActiveObject();
        window.activeCanvas.renderAll();
      }

      const text = new window.fabric.IText(this.$t("newlyAddedText"), {
        fontFamily: "JakartaSans",
        fill: "black",
        fontSize: 60,
        cornerStyle: "circle",
        selectable: true,
        textAlign: "center",
        pathAlign: "center",
        padding: 30,
      });

      text.setControlsVisibility(controlsVisibility);

      window.activeCanvas.viewportCenterObject(text);
      window.activeCanvas.add(text);
      window.activeCanvas.setActiveObject(text);

      text.enterEditing();
      text.selectAll();
      window.activeCanvas.renderAll();

      // Open text panel after layout settles (iOS: card slide can trigger viewport resize which closes panel)
      this.$nextTick(() => {
        setTimeout(() => {
          this.showTextPanel = true;
          this.syncPanelOpenToParent();
        }, 150);
      });
    },

    async onFileUpload(url, type = "image") {
      this.uploadType = type;
      if (this.uploadType === "image") {
        trackEvent({
          eventName: "Customizer",
          eventType: "Click",
          action: "AddImage",
        });
        const img = await window.fabric.FabricImage.fromURL(url, {
          crossOrigin: "anonymous",
        });
        img.set({ cornerStyle: "circle" });
        img.setControlsVisibility(controlsVisibility);
        img.scaleToWidth(300);
        window.activeCanvas.viewportCenterObject(img);
        window.activeCanvas.add(img);
        window.activeCanvas.setActiveObject(img);
      } else if (this.uploadType === "background") {
        trackEvent({
          eventName: "Customizer",
          eventType: "Click",
          action: "UploadBackgroundImage",
        });
        const existingBackgrounds = window.activeCanvas
          .getObjects()
          .filter((obj) => obj.isBackgroundImage);
        const existingImg = existingBackgrounds.find(
          (bg) => bg._element?.src === url || bg.getSrc?.() === url,
        );

        if (existingImg) {
          this.positionBackgroundImage(existingImg);
        } else {
          const img = await window.fabric.FabricImage.fromURL(url, {
            crossOrigin: "anonymous",
          });
          img.set({
            cornerStyle: "circle",
            isBackgroundImage: true,
          });
          img.setControlsVisibility(controlsVisibility);
          img.scaleToWidth(canvasWidth);
          window.activeCanvas.add(img);
          window.activeCanvas.viewportCenterObject(img);
          this.positionBackgroundImage(img);
        }

        this.fireDebouncedModify();
        this.$refs.backgroundRef.value = "";
      }
      this.fireDebouncedModify();
    },

    handlePaste(e) {
      this.$refs.addImagesPanel?.handlePaste(e);
    },

    async handleLoad() {
      await nextTick();
    },
  },

  beforeUnmount() {
    if (this._closePanelOnOutsideClick) {
      document.removeEventListener("click", this._closePanelOnOutsideClick, true);
    }
  },
  mounted() {
    this.handleLoad();
    this.fireDebouncedModify = debounce(() => {
      window.activeCanvas.fire("object:modified");
    }, 500);
    this.trackChangeBackground = debounce((type) => {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "ChangeBackground",
        details: { type },
      });
    }, 1000);

    document.addEventListener("click", (event) => {
      const el = event.target;
      if (
        el.tagName !== "CANVAS" &&
        !el.closest(".fontPanel") &&
        !el.closest(".textPanel") &&
        !el.closest(".colorPicker") &&
        !el.closest(".toolbar")
      ) {
        // Optional: Add any click handling logic
      }
    });

    this._closePanelOnOutsideClick = (event) => {
      if (!this.anyPanelOpen) return;
      if (event.target.closest(".editorPanel") || event.target.closest(".colorPicker")) return;
      this.closeAllPanels();
    };
    document.addEventListener("click", this._closePanelOnOutsideClick, true);

    const initialHeight = window.visualViewport.height;
    window.visualViewport.addEventListener("resize", () => {
      const currentHeight = window.visualViewport.height;
      const heightShrink = initialHeight - currentHeight;
      // Don't close text panel when user is editing (text selected) – keyboard open is a big shrink
      const isEditingText = this.selectedElement?.type?.toLowerCase().includes("text");
      if (heightShrink > 80 && !isEditingText) {
        this.showTextPanel = false;
      } else if (isEditingText) {
        this.showTextPanel = true;
      }
    });
  },
};
</script>

<style lang="scss">
#cardDesigner {
  .editorPanel {
    filter: drop-shadow(4px 0px 25px rgba(0, 0, 0, 0.2));
    background: var(--color-sol-black);
    z-index: 200;
    border: 1px solid var(--color-border-light-20);
    border-bottom: 0;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    @media (min-width: 640px) and (max-width: 1023px) {
      width: 640px !important;
      left: 50% !important;
      right: auto !important;
      transform: translateX(-50%);
    }
  }

  .toolbar {
    background: transparent;
  }

  .toolbarButton {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    min-height: 72px;
    background: var(--color-bg-nav-button);
    border-radius: 16px;
    border: 1px solid transparent;
    color: rgba(245, 248, 255, 0.85) !important;
    font-weight: 500;
    font-size: 10px;
    padding: 4px;
    margin: 5px 0;

    @media (min-width: 1024px) {
      gap: 10px;
      min-height: 80px;
      padding: 8px;
      font-size: 14px;
    }

    cursor: pointer;
    transition: border-color 0.2s ease,
    box-shadow 0.2s ease;

    &:hover {
      border-color: var(--color-nav-button-hover);
      box-shadow: 0 0 0 1px var(--color-nav-button-hover);

      .toolbarButton__icon:not(.toolbarButton__icon--selected) {
        fill-opacity: 0.95;
      }
    }

    &--selected {
      color: #f5f8ff !important;
      border-color: var(--color-nav-button-active);
      box-shadow: 0 0 0 2px var(--color-nav-button-active),
        0 0 12px var(--color-nav-button-active);
    }

    &__icon {
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      fill: currentColor;
      fill-opacity: 0.85;

      @media (min-width: 1024px) {
        width: 32px;
        height: 32px;
      }

      &--selected {
        fill-opacity: 1;
      }
    }

    &__label {
      line-height: 1.2;
      text-align: center;
    }
  }

  .scaleRangeSlider {
    appearance: none !important;
    width: 200px !important;
    padding: 0 !important;
    height: 4px;
    border-radius: 5px;
    background: linear-gradient(to right,
        var(--color-border-light) 0%,
        var(--color-border-light) var(--scale-progress, 0%),
        rgba(245, 248, 255, 0.4) var(--scale-progress, 0%),
        rgba(245, 248, 255, 0.4) 100%);
    outline: none;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: var(--color-border-light);
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: var(--color-border-light);
      cursor: pointer;
      border: none;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &::-moz-range-track {
      height: 4px;
      border-radius: 5px;
      background: linear-gradient(to right,
          var(--color-border-light) 0%,
          var(--color-border-light) var(--scale-progress, 0%),
          rgba(245, 248, 255, 0.4) var(--scale-progress, 0%),
          rgba(245, 248, 255, 0.4) 100%);
    }

    @media (max-width: 1023px) {
      display: none;
    }
  }

  .undoRedoContainer {
    margin: 0;
    z-index: auto;

    img {
      filter: brightness(0) invert(1);
      opacity: 0.9;

      &:hover {
        opacity: 1;
      }
    }

    .undo-redo-btn {
      background: var(--color-bg-toolbar) !important;
      border: none;
      border-radius: 12px;
      padding: 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s;

      img {
        width: 20px !important;
        height: 20px !important;
        filter: brightness(0) invert(1);
      }

      &:hover:not(.opacity-30) {
        opacity: 0.8;
      }
    }
  }

  @media (min-width: 1024px) {
    .toolbarButton {
      flex: 0 0 auto;
      width: 126px;
      height: 86px;
    }
  }

  .panelHidden {
    transform: translateY(100%) !important;

    @media (min-width: 640px) and (max-width: 1023px) {
      transform: translateX(-50%) translateY(100%) !important;
    }

    @media (min-width: 1024px) {
      transform: translate(420px, -50%) !important;
    }
  }

  .toolbarButton__label {
    overflow-wrap: anywhere;
  }
}
</style>
